import React, { useEffect, useMemo, useState } from "react";
import CustomModalLayout from "../../../web/Auth/CustomModalLayout";
import { Button } from "../../../../shadcn/components/ui/button";
import { FileDown, Loader2, Mail, Phone } from "lucide-react";
import { getBankByFilter } from "services";

const BID_FORM_OWNER_SERVICE = "bank-bid-application-form";

function pickBidApplicationFile(
  files: unknown,
): { uuid?: string; download_url?: string; file_name?: string } | null {
  if (!Array.isArray(files) || files.length === 0) return null;
  const match = files.find(
    (f: { owner_service?: string; uuid?: string }) =>
      f?.owner_service === BID_FORM_OWNER_SERVICE && Boolean(f?.uuid),
  );
  return (match as { uuid?: string; download_url?: string; file_name?: string }) ?? null;
}

export interface OfflineAuctionParticipationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selected: Record<string, unknown>;
}

export default function OfflineAuctionParticipationModal({
  open,
  setOpen,
  selected,
}: OfflineAuctionParticipationModalProps) {
  const [bankDetails, setBankDetails] = useState<{
    name?: string;
    files?: unknown[];
  } | null>(null);
  const [loadingBank, setLoadingBank] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const bankFromEvent = selected?.bank as
    | {
        uuid?: string;
        name?: string;
        bid_application_form_file_id?: {
          uuid?: string;
          download_url?: string;
        };
      }
    | undefined;

  const eventData = selected?.data as
    | { bank_uuid?: string; bank?: { uuid?: string } }
    | undefined;

  const bankUuid = useMemo(() => {
    return (
      bankFromEvent?.uuid ??
      eventData?.bank_uuid ??
      eventData?.bank?.uuid ??
      null
    );
  }, [bankFromEvent?.uuid, eventData?.bank?.uuid, eventData?.bank_uuid]);

  useEffect(() => {
    if (!open) {
      setBankDetails(null);
      setFetchError(null);
      setLoadingBank(false);
      return;
    }

    if (!bankUuid) {
      setBankDetails(null);
      setFetchError(null);
      setLoadingBank(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingBank(true);
      setFetchError(null);
      setBankDetails(null);
      try {
        const rows: unknown = await getBankByFilter({
          apiConfig: {
            includeRelations: ["files"],
          },
          limitBy: { per_page: 1, page: 1 },
          sortBy: [{ attribute: "created_at", order: "asc" }],
          filterBy: [
            {
              attribute: "uuid",
              op: "eq",
              value: bankUuid,
            },
          ],
        });

        if (cancelled) return;

        const list = Array.isArray(rows) ? rows : [];
        const row = (list[0] as { name?: string; files?: unknown[] }) || null;
        setBankDetails(row);
      } catch {
        if (!cancelled) {
          setFetchError("We could not load the latest bank information.");
          setBankDetails(null);
        }
      } finally {
        if (!cancelled) setLoadingBank(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, bankUuid]);

  const displayBankName =
    bankDetails?.name || bankFromEvent?.name || "";

  const bidFileFromApi = useMemo(
    () => pickBidApplicationFile(bankDetails?.files),
    [bankDetails?.files],
  );

  const bidFileFromEvent = useMemo(() => {
    const nested = bankFromEvent?.bid_application_form_file_id;
    if (nested?.uuid) return nested;
    return null;
  }, [bankFromEvent?.bid_application_form_file_id]);

  const bidFormFile = bankUuid ? bidFileFromApi : bidFileFromEvent;

  const downloadHref = useMemo(() => {
    if (!bidFormFile?.uuid) return null;
    if (bidFormFile.download_url) return bidFormFile.download_url;
    const base = process.env.NEXT_PUBLIC_IMAGIX_URL?.replace(/\/$/, "");
    return base ? `${base}/${bidFormFile.uuid}` : null;
  }, [bidFormFile]);

  const supportEmail = "support@auctionbazaar.com";
  const supportPhone = "+91-8370969696";
  const supportTelHref = `tel:${supportPhone.replace(/\s/g, "")}`;

  const showDownload = Boolean(downloadHref);

  return (
    <CustomModalLayout
      open={open}
      onClose={() => setOpen(false)}
      className="!w-[min(100vw-1.5rem,40rem)] max-h-[min(88vh,720px)] flex flex-col border border-gray-200 shadow-lg overflow-hidden mx-3 sm:mx-4"
      childrenClassName="flex flex-col min-h-0 flex-1 p-0"
    >
      <div className="shrink-0 border-b border-gray-100 px-4 pt-6 pb-4 pr-12 text-center">
        <h2 className="text-lg font-semibold text-brand-textBlack">
          Participating in this auction
        </h2>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">
          Online KYC is not enabled for this listing. The bank requires you to
          complete participation through the offline process at the branch.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Steps to participate
          </p>
          <ol className="space-y-4 text-sm text-gray-700 leading-relaxed text-left">
            <li>
              <span className="mr-2 font-semibold text-brand-headingBlue">1.</span>
              Visit the{" "}
              <span className="font-semibold text-brand-textBlack">
                {displayBankName ? `${displayBankName} branch` : "bank branch"}
              </span>{" "}
              for property details, auction terms, and registration. Use the
              branch on the auction notice when you can.
            </li>
            <li>
              <span className="mr-2 font-semibold text-brand-headingBlue">2.</span>
              Pay the{" "}
              <span className="font-semibold text-brand-textBlack">EMD</span> per
              bank rules, then complete the{" "}
              <span className="font-semibold text-brand-textBlack">
                bid application form
              </span>{" "}
              at the bank. Staff will confirm documents and signatures.
            </li>
            <li>
              <span className="mr-2 font-semibold text-brand-headingBlue">3.</span>
              For questions about the process, contact your bank representative or{" "}
              <span className="font-semibold text-brand-textBlack">
                Auction Bazaar support
              </span>
              . We can help explain the offline flow.
            </li>
          </ol>
        </section>

        <section className="rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Bid application form
          </p>
          {bankUuid && loadingBank ? (
            <div
              className="flex items-center gap-3 py-6 text-gray-500"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader2
                className="h-6 w-6 shrink-0 animate-spin text-brand-headingBlue"
                aria-hidden
              />
              <p className="text-sm">Checking for an uploaded form…</p>
            </div>
          ) : fetchError ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              {fetchError} You can still follow the steps above or contact
              support.
            </p>
          ) : showDownload && downloadHref ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                A reference copy is below. The bank may still require a signed hard
                copy at the branch.
              </p>
              <a
                href={downloadHref}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  type="button"
                  className="w-full bg-brand-headingBlue hover:bg-brand-headingBlue/90 text-white"
                >
                  <FileDown className="h-4 w-4 mr-2" aria-hidden />
                  Download bid application form
                </Button>
              </a>
            </div>
          ) : (
            <p className="text-sm text-gray-600 leading-relaxed">
              The form is usually issued at the branch. Contact the bank or
              Auction Bazaar support if you need help.
            </p>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Need help?
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center sm:gap-4">
            <a
              href={`mailto:${supportEmail}`}
              className="text-brand-headingBlue hover:underline inline-flex items-center justify-center gap-2"
            >
              <Mail className="h-4 w-4 shrink-0" aria-hidden />
              {supportEmail}
            </a>
            <a
              href={supportTelHref}
              className="text-brand-headingBlue hover:underline inline-flex items-center justify-center gap-2"
            >
              <Phone className="h-4 w-4 shrink-0" aria-hidden />
              {supportPhone}
            </a>
          </div>
        </section>
      </div>

      <div className="shrink-0 border-t border-gray-100 px-4 py-3">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setOpen(false)}
        >
          Close
        </Button>
      </div>
    </CustomModalLayout>
  );
}
